/**
 * お知らせ詳細ページ（シンプル版）
 */

import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EntityImageThumbnail } from "../../../components/ui/EntityImageThumbnail";
import { getImageUrl } from "../../../utils/imageFetcher";
import "./AnnounceDetailPage.css";

const adminMedia = (sp: string | undefined | null): string =>
  sp ? getImageUrl(sp) || "" : "";

// コンテンツメディア情報の型
interface ContentMediaItem {
  name: string;
  keyName?: string;
  key_name?: string;
  storagePath?: string;
  storage_path?: string;
  mediaType?: string;
  media_type?: string;
}

interface AnnounceDetailPageProps {
  announce: any;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onBack: () => void;
}

// 日付をフォーマット（曜日付きJST）
function formatDateTimeJST(value: any): string {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    const formatted = date.toLocaleString("ja-JP", options);
    const jstDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
    );
    const weekday = weekdays[jstDate.getDay()];
    return formatted.replace(/日\s/, `日(${weekday}) `);
  } catch {
    return String(value);
  }
}

export const AnnounceDetailPage: React.FC<AnnounceDetailPageProps> = ({
  announce,
  onEdit,
  onDelete,
  onBack,
}) => {
  // 画像情報を取得
  const thumbnailImage = announce?.thumbnailImage || announce?.thumbnail_image;
  const galleryImages =
    announce?.galleryImages || announce?.gallery_images || [];
  const videos = announce?.videos || [];
  const contentMedia: ContentMediaItem[] =
    announce?.contentMedia || announce?.content_media || [];

  // コンテンツ
  const content = announce?.content || "";

  // コンテンツメディアのマップを作成（keyName -> storagePath）
  const contentMediaMap = useMemo(() => {
    const map = new Map<string, { storagePath: string; mediaType: string }>();
    contentMedia.forEach((media) => {
      const keyName = media.keyName || media.key_name || "";
      const storagePath = media.storagePath || media.storage_path || "";
      const mediaType = media.mediaType || media.media_type || "image";
      if (keyName && storagePath) {
        map.set(keyName, { storagePath, mediaType });
      }
    });
    return map;
  }, [contentMedia]);

  // image:keyName と video:keyName を実際のメディアURLに変換する処理
  const processedContent = useMemo(() => {
    let processed = content;
    // image:keyName形式を処理
    processed = processed.replace(
      /!\[([^\]]*)\]\(image:([^)]+)\)/g,
      (match: string, alt: string, keyName: string) => {
        const media = contentMediaMap.get(keyName);
        if (media) {
          return `![${alt}](${adminMedia(media.storagePath)})`;
        }
        return match;
      }
    );
    // video:keyName形式を処理
    processed = processed.replace(
      /!\[([^\]]*)\]\(video:([^)]+)\)/g,
      (match: string, alt: string, keyName: string) => {
        const media = contentMediaMap.get(keyName);
        if (media) {
          // ReactMarkdownは動画を直接サポートしないので、特別な記法に変換
          return `[VIDEO:${keyName}:${adminMedia(media.storagePath)}]`;
        }
        return match;
      }
    );
    return processed;
  }, [content, contentMediaMap]);

  // ReactMarkdownのカスタムコンポーネント
  const markdownComponents = useMemo(
    () => ({
      p: ({ children, ...props }: any) => {
        // 子要素が文字列の場合、VIDEO:タグを処理
        if (typeof children === "string") {
          const videoMatch = children.match(/\[VIDEO:([^:]+):([^\]]+)\]/);
          if (videoMatch) {
            const [, , videoUrl] = videoMatch;
            return (
              <video
                controls
                style={{ maxWidth: "100%", display: "block", margin: "10px 0" }}
                src={videoUrl}
              >
                お使いのブラウザは動画タグをサポートしていません。
              </video>
            );
          }
        }
        // 子要素が配列の場合、VIDEO:タグを含む要素を処理
        if (Array.isArray(children)) {
          const processedChildren = children.map((child, index) => {
            if (typeof child === "string") {
              const videoMatch = child.match(/\[VIDEO:([^:]+):([^\]]+)\]/);
              if (videoMatch) {
                const [, , videoUrl] = videoMatch;
                return (
                  <video
                    key={index}
                    controls
                    style={{ maxWidth: "100%", display: "block", margin: "10px 0" }}
                    src={videoUrl}
                  >
                    お使いのブラウザは動画タグをサポートしていません。
                  </video>
                );
              }
            }
            return child;
          });
          return <p {...props}>{processedChildren}</p>;
        }
        return <p {...props}>{children}</p>;
      },
    }),
    []
  );

  // 全画像リストをギャラリー用に整理
  const allImages = useMemo(() => {
    const images = [];
    if (thumbnailImage) {
      images.push({ ...thumbnailImage, label: "サムネイル" });
    }
    galleryImages.forEach((img: any, index: number) => {
      images.push({ ...img, label: `ギャラリー ${index + 1}` });
    });
    return images;
  }, [thumbnailImage, galleryImages]);

  if (!announce) {
    return (
      <div className="announce-detail-page">
        <div className="empty-state">
          <p>お知らせが見つかりません</p>
          <button className="btn btn-primary" onClick={onBack}>
            一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="announce-detail-page">
      {/* ヘッダー */}
      <div className="announce-detail-header">
        <h1>お知らせ詳細</h1>
        <div className="announce-detail-actions">
          <button className="btn btn-outline" onClick={onBack}>
            一覧に戻る
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => onEdit(announce.id)}
          >
            編集
          </button>
          <button
            className="btn btn-danger"
            onClick={() => onDelete(announce.id)}
          >
            削除
          </button>
        </div>
      </div>

      <div className="announce-detail-content">
        {/* 基本情報セクション */}
        <section className="detail-section">
          <h2 className="section-title">基本情報</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>ID:</label>
              <span>{announce.id}</span>
            </div>
            <div className="detail-item">
              <label>タイトル:</label>
              <span>{announce.title}</span>
            </div>
            <div className="detail-item">
              <label>公開日:</label>
              <span>
                {formatDateTimeJST(announce.publishDate || announce.publish_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>表示優先度:</label>
              <span>
                {announce.displayPriority ?? announce.display_priority ?? 0}
              </span>
            </div>
            <div className="detail-item">
              <label>公開状態:</label>
              <span
                className={`status-badge ${
                  announce.isPublished ?? announce.is_published
                    ? "enabled"
                    : "disabled"
                }`}
              >
                {announce.isPublished ?? announce.is_published ? "公開" : "非公開"}
              </span>
            </div>
          </div>
        </section>

        {/* 画像セクション */}
        {allImages.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">画像</h2>
            <div className="image-gallery-grid">
              {allImages.map((img: any, index: number) => {
                const storagePath = img.storagePath || img.storage_path;
                return (
                  <div key={index} className="image-gallery-item">
                    <div className="image-gallery-preview">
                      <EntityImageThumbnail
                        storagePath={storagePath}
                        alt={img.name || img.label}
                        size={160}
                      />
                    </div>
                    <div className="image-gallery-info">
                      <span className="image-label">{img.label}</span>
                      {img.caption && (
                        <span className="image-caption">{img.caption}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* 動画セクション */}
        {videos.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">動画</h2>
            <div className="video-list">
              {videos.map((video: any, index: number) => {
                const storagePath = video.storagePath || video.storage_path;
                return (
                  <div key={index} className="video-item">
                    <div className="video-info">
                      <span className="video-name">{video.name || `動画 ${index + 1}`}</span>
                      {video.caption && (
                        <span className="video-caption">{video.caption}</span>
                      )}
                    </div>
                    {storagePath && (
                      <video
                        controls
                        className="video-player"
                        src={adminMedia(storagePath)}
                      >
                        お使いのブラウザは動画タグをサポートしていません。
                      </video>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* コンテンツメディアセクション */}
        {contentMedia.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">コンテンツメディア</h2>
            <div className="content-media-grid">
              {contentMedia.map((media: ContentMediaItem, index: number) => {
                const storagePath = media.storagePath || media.storage_path || "";
                const keyName = media.keyName || media.key_name || "";
                const mediaType = media.mediaType || media.media_type || "image";
                return (
                  <div key={index} className="content-media-item">
                    <div className="content-media-preview">
                      {mediaType === "video" ? (
                        <video
                          controls
                          style={{ maxWidth: "100%", maxHeight: "120px" }}
                          src={adminMedia(storagePath)}
                        />
                      ) : (
                        <EntityImageThumbnail
                          storagePath={storagePath}
                          alt={media.name}
                          size={120}
                        />
                      )}
                    </div>
                    <div className="content-media-info">
                      <span className="media-name">{media.name}</span>
                      <span className="media-key">キー: {keyName}</span>
                      <span className="media-type">{mediaType === "video" ? "動画" : "画像"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* コンテンツプレビューセクション */}
        <section className="detail-section">
          <h2 className="section-title">内容プレビュー</h2>
          <div className="preview-container">
            <div className="preview-content markdown-preview">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {processedContent}
              </ReactMarkdown>
            </div>
          </div>

          {/* 生のMarkdownも表示 */}
          <div className="raw-content-section">
            <h3>生のMarkdown</h3>
            <pre className="raw-content">{content}</pre>
          </div>
        </section>

        {/* システム情報セクション */}
        <section className="detail-section">
          <h2 className="section-title">システム情報</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>作成日時:</label>
              <span>
                {formatDateTimeJST(announce.createDate || announce.create_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>更新日時:</label>
              <span>
                {formatDateTimeJST(announce.updateDate || announce.update_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>バージョン:</label>
              <span>{announce.version || "-"}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
